import React, { useState, useEffect, useRef } from 'react';
import { Stack, IconButton, Typography, Menu, MenuItem, ListItemIcon, ListItemText, Tooltip, TextField, InputAdornment, Box } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';

const RecordingHeader = ({
  onBack,
  onOpenMenu,
  onCloseMenu,
  anchorEl,
  onDelete,
  onSetInitialTime,
  hasInitialTime = false,
  title,
  onUpdateTitle,
  starred,
  onToggleStar,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedTitle, setEditedTitle] = useState(title || "");
  const [showTitle, setShowTitle] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.select();
    }
  }, [isEditing]);

  useEffect(() => {
    setEditedTitle(title || "");
  }, [title]);

  // Show the title with animation when it changes or component mounts
  useEffect(() => {
    if (!isEditing && title) {
      setShowTitle(false);
      // Small delay before showing title to ensure animation triggers
      const timer = setTimeout(() => {
        setShowTitle(true);
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [title, isEditing]);

  const handleStartEditing = () => {
    setEditedTitle(title || "");
    setIsEditing(true);
  };

  const handleCancelEditing = () => {
    setIsEditing(false);
  };

  const handleSaveTitle = () => {
    if (editedTitle.trim()) {
      onUpdateTitle(editedTitle.trim());
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSaveTitle();
    } else if (e.key === 'Escape') {
      handleCancelEditing();
    }
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{ mb: 3, width: '100%' }}
    >
      <IconButton
        onClick={onBack}
        sx={{
          backgroundColor: (theme) => theme.palette.grey[100],
          padding: { xs: 1, sm: 1.5 },
          '&:hover': {
            backgroundColor: (theme) => theme.palette.grey[200],
          },
        }}
      >
        <ArrowBackIcon />
      </IconButton>

      <Tooltip title={starred ? "Հdelays აստղ" : "Ադèlays აստղ"}>
        <IconButton
          onClick={onToggleStar}
          aria-label={starred ? "Unstar" : "Star"}
          sx={{
            color: starred ? 'warning.main' : 'text.secondary',
            mr: 1,
            '&:hover': {
              color: 'warning.main'
            }
          }}
        >
          {starred ? <StarIcon /> : <StarBorderIcon />}
        </IconButton>
      </Tooltip>

      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        {isEditing ? (
          <TextField
            fullWidth
            autoFocus
            inputRef={inputRef}
            variant="outlined"
            value={editedTitle}
            onChange={(e) => setEditedTitle(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Մուտքագրեք վերնագիրը"
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              }
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={handleSaveTitle}
                    sx={{ color: 'primary.main' }}
                  >
                    <CheckIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={handleCancelEditing}
                    sx={{ color: 'text.secondary' }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        ) : (
          <Tooltip title={title || "Ձայնագրության մանրամասները"} placement="bottom-start">
            <Box
              onClick={handleStartEditing}
              sx={{
                position: 'relative',
                cursor: 'pointer',
                width: '100%',
                '&:hover .edit-icon': { opacity: 1 },
                '&:hover .title-text': { color: 'primary.main' }
              }}
            >
              <Typography
                variant="h6"
                component="h1"
                className="title-text"
                sx={{
                  fontSize: { xs: '1.15rem', sm: '1.25rem' },
                  fontWeight: 600,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  transition: 'color 0.2s ease',
                  visibility: showTitle ? 'visible' : 'hidden',
                  animation: showTitle ? 'fadeIn 0.5s ease-out' : 'none',
                  '@keyframes fadeIn': {
                    '0%': {
                      opacity: 0,
                    },
                    '100%': {
                      opacity: 1,
                    }
                  }
                }}
              >
                {title || "Ձայնագրության մանրամասները"}
              </Typography>
              <EditIcon className="edit-icon" sx={{
                position: 'absolute',
                right: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                opacity: 0,
                transition: 'opacity 0.2s ease',
                fontSize: '1.2rem',
                pointerEvents: 'none'
              }} />
            </Box>
          </Tooltip>
        )}
      </Box>

      <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: 2, flexShrink: 0 }}>
        <IconButton onClick={onOpenMenu} aria-label="Գործողություններ">
          <MoreVertIcon />
        </IconButton>
      </Stack>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={onCloseMenu}
        elevation={3}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            minWidth: '200px',
            mt: 1,
            '& .MuiMenuItem-root': {
              py: 1.5,
            },
          },
        }}
      >
        {onSetInitialTime && (
          <MenuItem onClick={onSetInitialTime} sx={{ color: 'text.primary' }}>
            <ListItemIcon>
              <AccessTimeIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={hasInitialTime ? 'Փոխել սկզբնական ժամը' : 'Սահմանել սկզբնական ժամ'} />
          </MenuItem>
        )}
        <MenuItem
          onClick={onDelete}
          sx={{
            color: 'text.primary',
            '&:hover': {
              color: 'error.main',
              '& .MuiListItemIcon-root': {
                color: 'error.main',
              },
            },
          }}
        >
          <ListItemIcon>
            <DeleteOutlineIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Ջնջել" />
        </MenuItem>
      </Menu>
    </Stack>
  );
};

export default RecordingHeader;
